import { MonitorStatus } from '@fe/core';
import { type FilterDescriptor } from '@progress/kendo-data-query';
import { FlightStatus } from '../../common/enums';
import { DashboardWidgetSettings } from '../../types/settings';
import { DashboardWidget, INITIAL_WIDGETS } from '../features/dashboard/Dashboard';

export const dashboardFlightFilters = {
    holding: [{ field: 'holding', operator: 'eq', value: true }],
    diverting: [{ field: 'diverting', operator: 'eq', value: true }],
    emergency: [{ field: 'monitorStatus', operator: 'eq', value: MonitorStatus.emergency }],
    'go-around': [{ field: 'goAround', operator: 'eq', value: true }],
    'taxi-times': [{ field: 'extra.taxiTime', operator: 'isnotnull' }],
    monitored: [
        { field: 'status', operator: 'eq', value: FlightStatus.Active },
        { field: 'monitorStatus', operator: 'neq', value: MonitorStatus.null }
    ],
    'position-updated': [
        {
            field: 'positionUpdateThresholdBreached',
            operator: 'gte',
            value: MonitorStatus.info
        }
    ]
} satisfies Record<string, FilterDescriptor[]>;

export const settingsAreEqual = (first: DashboardWidgetSettings[], second: DashboardWidgetSettings[]): boolean =>
    first.length === second.length &&
    first.every((item, index) => {
        const other = second[index];
        return (
            item.id === other.id &&
            item.enabled === other.enabled &&
            item.x === other.x &&
            item.y === other.y &&
            item.w === other.w &&
            item.h === other.h
        );
    });

export const toWidgetSettings = (widgets: DashboardWidget[]): DashboardWidgetSettings[] =>
    widgets.map(({ id, enabled, layout: { x, y, w, h } }) => ({ id, enabled, x, y, w, h }));

export const mergeWidgets = (saved: DashboardWidgetSettings[] | null): DashboardWidget[] => {
    if (!saved) {
        return INITIAL_WIDGETS;
    }

    const savedById = new Map(saved.map((item) => [item.id, item]));

    return INITIAL_WIDGETS.map((widget) => {
        const item = savedById.get(widget.id);

        if (!item) {
            return widget;
        }

        return {
            ...widget,
            enabled: item.enabled,
            layout: { ...widget.layout, x: item.x, y: item.y, w: item.w, h: item.h }
        };
    });
};

export const DASHBOARD_COUNT_ORDER = [
    'holding',
    'diverting',
    'emergency',
    'goAround',
    'taxiTimes',
    'monitoredActive',
    'monitoredPlanned',
    'monitoredScheduled',
    'positionUpdated'
] as const;
export type DashboardCountKey = (typeof DASHBOARD_COUNT_ORDER)[number];

export const DASHBOARD_COUNT_INDEX = Object.fromEntries(DASHBOARD_COUNT_ORDER.map((key, index) => [key, index])) as Record<
    DashboardCountKey,
    number
>;

const WIDGET_COUNTS: Record<string, readonly DashboardCountKey[] | undefined> = {
    holding: ['holding'],
    diverting: ['diverting'],
    emergency: ['emergency'],
    goAround: ['goAround'],
    taxiTimes: ['taxiTimes'],
    positionUpdated: ['positionUpdated'],
    flightStatsChart: ['monitoredActive', 'monitoredPlanned', 'monitoredScheduled']
};

export function hasDashboardWidgetRecords(widgetId: string, counts: readonly number[] | null): boolean {
    const keys = WIDGET_COUNTS[widgetId];
    // Unknown counts keep a widget visible until the query completes.
    return keys === undefined || !keys.every((key) => counts?.[DASHBOARD_COUNT_INDEX[key]] === 0);
}

export function buildDashboardSummaryFilters(
    base: Record<string, FilterDescriptor[]>,
    statuses: { planned: unknown; scheduled: unknown }
): FilterDescriptor[][] {
    const monitoredFor = (value: unknown): FilterDescriptor[] => [
        ...base.monitored.filter((filter) => filter.field !== 'status'),
        { field: 'status', operator: 'eq', value }
    ];
    const filters: Record<DashboardCountKey, FilterDescriptor[]> = {
        holding: base.holding,
        diverting: base.diverting,
        emergency: base.emergency,
        goAround: base['go-around'],
        taxiTimes: base['taxi-times'],
        monitoredActive: base.monitored,
        monitoredPlanned: monitoredFor(statuses.planned),
        monitoredScheduled: monitoredFor(statuses.scheduled),
        positionUpdated: [{ field: 'positionUpdateThresholdBreached', operator: 'eq', value: true }]
    };
    return DASHBOARD_COUNT_ORDER.map((key) => filters[key]);
}

export const DASHBOARD_COLUMNS = 12;
export type Placement = { x: number; y: number; w: number; h: number };
type LayoutConstraints = { minW?: number; minH?: number };

export function toPlacement({ x, y, w, h }: Placement): Placement {
    return { x, y, w, h };
}

export function samePlacement(first: Placement, second: Placement): boolean {
    return first.x === second.x && first.y === second.y && first.w === second.w && first.h === second.h;
}

export function overlaps(first: Placement, second: Placement): boolean {
    return first.x < second.x + second.w && first.x + first.w > second.x && first.y < second.y + second.h && first.y + first.h > second.y;
}

export function normalizePlacement(placement: Placement, constraints: LayoutConstraints): Placement {
    const w = Math.min(DASHBOARD_COLUMNS, Math.max(placement.w, constraints.minW ?? 1));
    return {
        ...toPlacement(placement),
        w,
        h: Math.max(placement.h, constraints.minH ?? 1),
        x: Math.max(0, Math.min(placement.x, DASHBOARD_COLUMNS - w)),
        y: Math.max(0, placement.y)
    };
}

/** Preserve input order, metadata, sizes and disabled panels. Never mutate input. */
export function compactWidgets<T extends { id: string; enabled: boolean; layout: Placement }>(
    widgets: T[],
    allowHorizontalMovement = false
): T[] {
    const placed: Placement[] = [];
    const positions = new Map<string, Placement>();
    const ordered = widgets.filter((widget) => widget.enabled).sort((a, b) => a.layout.y - b.layout.y || a.layout.x - b.layout.x);

    for (const widget of ordered) {
        const { w, h } = widget.layout;
        const preferredX = allowHorizontalMovement ? Math.max(0, Math.min(widget.layout.x, DASHBOARD_COLUMNS - w)) : widget.layout.x;
        const columns = allowHorizontalMovement
            ? [preferredX, ...Array.from({ length: DASHBOARD_COLUMNS - w + 1 }, (_, x) => x).filter((x) => x !== preferredX)]
            : [preferredX];
        const maxY = placed.reduce((max, item) => Math.max(max, item.y + item.h), 0);

        search: for (let y = 0; y <= maxY; y++) {
            for (const x of columns) {
                const candidate = { x, y, w, h };
                if (placed.some((item) => overlaps(item, candidate))) continue;
                placed.push(candidate);
                positions.set(widget.id, candidate);
                break search;
            }
        }
    }

    return widgets.map((widget) => {
        const position = positions.get(widget.id);
        return position ? { ...widget, layout: { ...widget.layout, ...position } } : widget;
    });
}
